import React from 'react';
import PropTypes from 'prop-types';

import {Tooltip} from '@stubhub/bamboo';
import AdvisoryPriceDisplay from '@stubhub/react-advisory-price-display';

import './index.scss';

const PERCENTAGE = 100;

const DotHandle = ({
  id,
  text,
  setRef = () => {
    // Do nothing
  },
}) => {
  return (
    <Tooltip
      id={id}
      tooltipText={text}
      placement="top"
      isOpen
      className="price-slider__price-bubble"
      fallbackPlacements={[]}
    >
      <span className="price-slider__dot-handler-container">
        <span className="price-slider__dot-handler" ref={setRef} />
      </span>
    </Tooltip>
  );
};

DotHandle.propTypes = {
  setRef: PropTypes.func,
  id: PropTypes.string,
  text: PropTypes.node,
};

const Handle = ({
  setRef = () => {
    // Do nothing
  },
  pos = 0,
  children,
  className = '',
}) => {
  return (
    <span className={`price-slider__handle ${className}`} ref={setRef} style={{left: `${pos}%`}}>
      {children}
    </span>
  );
};

Handle.propTypes = {
  setRef: PropTypes.func,
  pos: PropTypes.number,
  children: PropTypes.node,

  /**
   * The Class Name used to override the Styling for the component.
   *
   */
  className: PropTypes.string,
};

const SlideBar = ({
  setRef = () => {
    // Do nothing
  },
}) => <span className="price-slider__slider-bar" ref={setRef} />;

SlideBar.propTypes = {
  setRef: PropTypes.func,
};

const SlideBarHighlight = ({max, min}) => (
  <span
    className="price-slider__slider-bar--highlight"
    style={{
      width: `${Math.abs(max - min) * PERCENTAGE}%`,
      left: `${max > min ? min * PERCENTAGE : max * PERCENTAGE}%`,
    }}
  />
);

SlideBarHighlight.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
};

function dragHandler(
  handleMinDotEl,
  handleMaxDotEl,
  slideBarEl,
  onChange = () => {
    // Do nothing
  }
) {
  // The X distance from the slider's left bound and the bound of its parent
  const handleMinEl = handleMinDotEl.parentElement.parentElement;
  const handleMaxEl = handleMaxDotEl.parentElement.parentElement;

  /*
   * Assuming both handles are same size circles
   * orginal handle and tooltip styling
   */
  let ORGINAL_BUBBLE_LEFT;
  let ORIGINAL_DOT_HANDLE_SIZE;
  if (handleMinDotEl) {
    ORIGINAL_DOT_HANDLE_SIZE = handleMinDotEl.style.width;
    ORGINAL_BUBBLE_LEFT = handleMinDotEl.style.left;
  }
  const ORGINAL_BUBBLE_TOP = '0px';

  // ENLARGED OFFSETS
  // eslint-disable-next-line no-magic-numbers
  const TOP_OFFSET = `${parseInt((30 - 16 - 8) * -1).toString()}px`; // This is the increased size minus the original size minus the bar height
  const ENLARGED_HEIGHT_AND_WIDTH = '30px';
  const LEFT_OFFSET = '0px';

  // Space Allowed Between the two Price Bubbles
  const boundaryBetweenToggle = 20;

  const min = 0;

  const handleMin = {
    posStart: 0,
    posEnd: 0,
  };
  const handleMax = {
    posStart: 0,
    posEnd: 0,
  };

  handleMinEl.addEventListener('mousedown', dragHandleMinMouseDown);
  handleMaxEl.addEventListener('mousedown', dragHandleMaxMouseDown);
  handleMinEl.addEventListener('touchstart', dragHandleTouchMin);
  handleMaxEl.addEventListener('touchstart', dragHandleTouchMax);

  function enlargeHandle(handleCircle) {
    if (handleCircle) {
      // On touch enlarge the handle
      handleCircle.style.width = ENLARGED_HEIGHT_AND_WIDTH;
      handleCircle.style.height = ENLARGED_HEIGHT_AND_WIDTH;
      handleCircle.style.top = TOP_OFFSET;
      handleCircle.style.left = LEFT_OFFSET;
    }
  }

  function dragHandleMinMouseDown(e) {
    e = e || window.event;
    e.preventDefault();
    // Get the mouse cursor position at startup:
    handleMin.posStart = getClientX(e);
    document.onmouseup = closeDragElement;
    // Call a function whenever the cursor moves:
    document.onmousemove = handleDragMinPrice;

    enlargeHandle(handleMinDotEl);
  }

  function dragHandleMaxMouseDown(e) {
    e = e || window.event;
    e.preventDefault();
    // Get the mouse cursor position at startup:
    handleMax.posStart = getClientX(e);
    document.onmouseup = closeDragElement;
    // Call a function whenever the cursor moves:

    document.onmousemove = handleDragMaxPrice;

    enlargeHandle(handleMaxDotEl);
  }

  function dragHandleTouchMin(e) {
    e = e || window.event;
    e.preventDefault();

    handleMinEl.addEventListener('touchmove', handleDragMinPrice);

    enlargeHandle(handleMinDotEl);
    handleMin.posStart = getClientX(e);

    handleMinEl.addEventListener('touchend', handleTouchEnd);
  }

  function dragHandleTouchMax(e) {
    e = e || window.event;
    e.preventDefault();

    handleMaxEl.addEventListener('touchmove', handleDragMaxPrice);

    enlargeHandle(handleMaxDotEl);
    handleMax.posStart = getClientX(e);

    handleMaxEl.addEventListener('touchend', handleTouchEnd);
  }

  function getClientX(e) {
    // Radius causes jump in the X location of handle
    if (e.changedTouches) {
      return e.changedTouches[0].clientX - e.changedTouches[0].radiusX;
    }

    return e.clientX;
  }

  function valueWithinBounds(minPos, maxPos, pos) {
    let result = pos;

    if (pos >= maxPos) {
      result = maxPos;
    } else if (pos <= minPos) {
      result = minPos;
    } else {
      result = pos;
    }

    return result;
  }

  // INTERVAL

  function handleDragMaxPrice(e) {
    // Calculate the new cursor position:
    handleMax.posEnd = handleMax.posStart - getClientX(e);
    handleMax.posStart = getClientX(e);

    const max = slideBarEl.getBoundingClientRect().width.toFixed(0);

    // Set the element's new position:
    const pos = handleMaxEl.offsetLeft - handleMax.posEnd;
    const minBound = Math.floor(handleMinEl.offsetLeft) + boundaryBetweenToggle;

    handleMaxEl.style.left = `${valueWithinBounds(minBound, max, pos)}px`;

    onChange({min: handleMinEl.offsetLeft / max, max: handleMaxEl.offsetLeft / max});
  }

  function handleDragMinPrice(e) {
    // Calculate the new cursor position:
    handleMin.posEnd = handleMin.posStart - getClientX(e);
    handleMin.posStart = getClientX(e);

    const max = slideBarEl.getBoundingClientRect().width.toFixed(0);

    // Get the element's new position:
    const pos = handleMinEl.offsetLeft - handleMin.posEnd;

    const maxBound = Math.floor(handleMaxEl.offsetLeft) - boundaryBetweenToggle;
    handleMinEl.style.left = `${valueWithinBounds(min, maxBound, pos)}px`;
    onChange({min: handleMinEl.offsetLeft / max, max: handleMaxEl.offsetLeft / max});
  }

  function resetCircleHandle() {
    if (handleMinDotEl && handleMaxDotEl) {
      handleMinDotEl.style.width = ORIGINAL_DOT_HANDLE_SIZE;
      handleMinDotEl.style.height = ORIGINAL_DOT_HANDLE_SIZE;
      handleMinDotEl.style.top = ORGINAL_BUBBLE_TOP;
      handleMinDotEl.style.left = ORGINAL_BUBBLE_LEFT;

      handleMaxDotEl.style.width = ORIGINAL_DOT_HANDLE_SIZE;
      handleMaxDotEl.style.height = ORIGINAL_DOT_HANDLE_SIZE;
      handleMaxDotEl.style.top = ORGINAL_BUBBLE_TOP;
      handleMaxDotEl.style.left = ORGINAL_BUBBLE_LEFT;
    }
  }

  function closeDragElement() {
    /* Stop moving when mouse button is released:*/
    document.onmouseup = null;
    document.onmousemove = null;
    resetCircleHandle();
  }

  function handleTouchEnd() {
    handleMinEl.removeEventListener('touchmove', handleDragMinPrice);
    handleMaxEl.removeEventListener('touchmove', handleDragMaxPrice);

    handleMinEl.removeEventListener('touchend', handleTouchEnd);
    handleMaxEl.removeEventListener('touchend', handleTouchEnd);

    resetCircleHandle();
  }
}

class SliderWithHandles extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentMin: 0,
      currentMax: 1,
      baseMin: 0,
      baseMax: 1,
    };

    this.handleChange.bind(this);
  }

  setMinHandleRef = (element) => {
    this.minHandleRef = element;
  };
  setMaxHandleRef = (element) => {
    this.maxHandleRef = element;
  };
  setSlideBarRef = (element) => {
    this.slideBarRef = element;
  };

  handleChange = ({min, max}) => {
    const {onChange} = this.props;
    if (typeof onChange === 'function') {
      onChange({min, max});
    }

    this.currentMin = min;
    this.currentMax = max;
  };

  componentDidMount() {
    dragHandler(this.minHandleRef, this.maxHandleRef, this.slideBarRef, this.handleChange);
  }

  UNSAFE_componentWillMount() {
    const {max, min, sliders} = this.props;
    const {baseMax, baseMin} = this.state;
    this.priceIncrement = find_bucket(max - min);
    this.setState({
      baseMin: 0,
      baseMax: 1,
    });
    let minPercentage = baseMin;
    let maxPercentage = baseMax;
    if (sliders.sliderMin) {
      minPercentage = Number.parseFloat(sliders.sliderMin[1]) / PERCENTAGE;
    }
    if (sliders.sliderMax) {
      maxPercentage = Number.parseFloat(sliders.sliderMax[1]) / PERCENTAGE;
    }
    this.currentMin = minPercentage;
    this.currentMax = maxPercentage;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {max, sliders: {sliderMin, sliderMax} = {}} = this.props;
    const {baseMax, baseMin} = this.state;
    if (max !== nextProps.max) {
      this.priceIncrement = find_bucket(nextProps.max - nextProps.min);
    }

    if (sliderMin !== undefined && nextProps.sliders.sliderMin === undefined) {
      this.currentMin = baseMin;
    }

    if (sliderMax !== undefined && nextProps.sliders.sliderMax === undefined) {
      this.currentMax = baseMax;
    }
  }

  set currentMin(val) {
    const {baseMin, currentMin} = this.state;
    if (val !== undefined && val !== null && val >= 0 && val !== currentMin) {
      this.setState({
        currentMin: val >= baseMin ? val : baseMin,
      });
    }
  }

  set currentMax(val) {
    const {baseMax, currentMax} = this.state;
    if (val !== undefined && val !== null && val >= 0 && val !== currentMax) {
      this.setState({
        currentMax: val <= baseMax ? val : baseMax,
      });
    }
  }

  get currentMin() {
    const {currentMin} = this.state;

    return this.adjusted_price(currentMin);
  }

  get currentMax() {
    const {currentMax} = this.state;

    return this.adjusted_price(currentMax);
  }

  adjusted_price(positionPercentage) {
    const {min, max} = this.props;
    const {priceIncrement} = this;
    const bucketCount = ((max - min) / priceIncrement).toFixed(0);
    const currentBucketPosition = positionPercentage * bucketCount;
    const dollarValue = (Math.floor(currentBucketPosition) + Math.round(min / priceIncrement)) * priceIncrement;
    if (dollarValue <= min || currentBucketPosition <= 0) {
      return min;
    }
    if (Math.round(currentBucketPosition) * priceIncrement + min >= max || currentBucketPosition >= bucketCount) {
      return max;
    }

    return dollarValue;
  }

  render() {
    const {currency, maxTicketPrice, max, advisoryCurrency} = this.props;
    const areTicketsOverPriceThreshold = max < maxTicketPrice && this.currentMax === max;
    const {currentMin, currentMax} = this.state;
    const dollarMin = (
      <AdvisoryPriceDisplay price={this.currentMin} currency={currency} advisoryCurrency={advisoryCurrency} />
    );
    const dollarMax = (
      <AdvisoryPriceDisplay price={this.currentMax} currency={currency} advisoryCurrency={advisoryCurrency} />
    );

    return (
      <div className="PricePerTicket__priceSlider">
        <Handle key="minHandle" pos={currentMin * PERCENTAGE}>
          <DotHandle id="minHandle-tooltip" text={dollarMin} setRef={this.setMinHandleRef} />
        </Handle>
        <Handle
          key="maxHandle"
          pos={currentMax * PERCENTAGE}
          className={areTicketsOverPriceThreshold ? 'priceSlider__price--over-threshold' : ''}
        >
          <DotHandle id="maxHandle-tooltip" text={dollarMax} setRef={this.setMaxHandleRef} />
        </Handle>
        <div className="price-slider__slider-bar-container">
          <SlideBarHighlight max={currentMax} min={currentMin} />
          <SlideBar setRef={this.setSlideBarRef} min={0} max={100} />
        </div>
      </div>
    );
  }
}

SliderWithHandles.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  maxTicketPrice: PropTypes.number,
  sliders: PropTypes.object.isRequired,
  currency: PropTypes.string,
  onChange: PropTypes.func,
  advisoryCurrency: PropTypes.string,
};

function find_bucket(priceDifferenceToBucket) {
  /* eslint-disable no-magic-numbers */
  const range = [
    [20, 1],
    [50, 2],
    [200, 5],
    [500, 10],
    [1000, 20],
    [10000, 50],
  ];
  /* eslint-enable no-magic-numbers */
  let start = 0;
  let end = range.length - 1;
  let currentPos;
  let lastPos = 0;
  while (start !== end) {
    currentPos = Math.floor((start + end) / 2);
    if (range[currentPos][0] > priceDifferenceToBucket) {
      end = currentPos;
    }

    if (range[currentPos][0] <= priceDifferenceToBucket) {
      start = currentPos;
    }
    if (lastPos === currentPos) {
      break;
    }
    lastPos = currentPos;
  }

  return range[currentPos][1];
}

export default SliderWithHandles;
